/**
 * Medicine alarm scheduling.
 *
 * Replaces `checkPatientAlarms` plus the `firedKeys` / `snoozedUntil` bookkeeping
 * that lived on the global `window.capsuleAlarm` object. Scanning is driven by
 * the shared ticker instead of yet another uncleared `setInterval`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { dueDoses, doseAlarmKey } from '../domain/medication';
import { useInterval } from './usePolling';
import { alarmService } from '../services/alarmService';

/** How often to look for a due dose. 30s is well inside the 60s tolerance window. */
const SCAN_INTERVAL_MS = 30000;

/** Snooze length, matching the legacy modal's button. */
export const SNOOZE_MINUTES = 10;

/** Keys already fired today, so a reload does not re-announce a taken dose. */
const FIRED_KEYS_STORAGE = 'capsule_notified_alarms';

function loadFiredKeys() {
  try {
    const raw = localStorage.getItem(FIRED_KEYS_STORAGE);
    if (!raw) return { date: '', keys: [] };
    const parsed = JSON.parse(raw);
    return { date: parsed.date || '', keys: Array.isArray(parsed.keys) ? parsed.keys : [] };
  } catch {
    return { date: '', keys: [] };
  }
}

function saveFiredKeys(dateKey, keys) {
  try {
    localStorage.setItem(FIRED_KEYS_STORAGE, JSON.stringify({ date: dateKey, keys: [...keys] }));
  } catch {
    /* non-critical */
  }
}

const localDateKey = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

/**
 * @param {Array} medicines   Records carrying `id`, `firstDoseTime`, `frequency`, `alarmEnabled`.
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {string|null} [options.ringtone]  Data URI for a custom ringtone.
 */
export function useMedicineAlarm(medicines = [], options = {}) {
  const { enabled = true, ringtone = null } = options;

  const [activeAlarm, setActiveAlarm] = useState(null);
  const firedKeys = useRef(new Set());
  const snoozedUntil = useRef(new Map());
  const currentDay = useRef(localDateKey());

  // Restore today's fired keys once, so a refresh mid-day is not noisy.
  useEffect(() => {
    const stored = loadFiredKeys();
    if (stored.date === localDateKey()) firedKeys.current = new Set(stored.keys);
  }, []);

  useEffect(() => {
    alarmService.setCustomRingtone(ringtone);
  }, [ringtone]);

  useEffect(() => () => alarmService.stop(), []);

  const dismiss = useCallback(() => {
    alarmService.stop();
    setActiveAlarm(null);
  }, []);

  /** Silence now, re-fire in `SNOOZE_MINUTES`. */
  const snooze = useCallback(() => {
    if (activeAlarm?.med?.id !== undefined) {
      snoozedUntil.current.set(
        String(activeAlarm.med.id),
        Date.now() + SNOOZE_MINUTES * 60 * 1000,
      );
      // Let the same dose fire again once the snooze expires.
      firedKeys.current.delete(activeAlarm.key);
    }
    alarmService.stop();
    setActiveAlarm(null);
  }, [activeAlarm]);

  /** Mark the dose taken: silence it and keep it suppressed for the rest of the day. */
  const markTaken = useCallback(() => {
    if (activeAlarm?.key) {
      firedKeys.current.add(activeAlarm.key);
      saveFiredKeys(currentDay.current, firedKeys.current);
    }
    alarmService.stop();
    setActiveAlarm(null);
  }, [activeAlarm]);

  const scan = useCallback(() => {
    if (!enabled || activeAlarm) return;

    const now = new Date();
    const today = localDateKey(now);

    // A new day clears yesterday's history.
    if (today !== currentDay.current) {
      currentDay.current = today;
      firedKeys.current = new Set();
      saveFiredKeys(today, firedKeys.current);
    }

    const due = dueDoses(medicines, firedKeys.current, now);
    const next = due.find((entry) => {
      const until = snoozedUntil.current.get(String(entry.med.id));
      return !until || Date.now() >= until;
    });

    if (!next) return;

    firedKeys.current.add(next.key);
    saveFiredKeys(today, firedKeys.current);
    setActiveAlarm(next);
    alarmService.start();
  }, [enabled, activeAlarm, medicines]);

  useInterval(scan, enabled ? SCAN_INTERVAL_MS : null, {
    immediate: true,
    pauseWhenHidden: false, // an alarm must fire even on a backgrounded tab
  });

  /** Fire an alarm on demand — used by the "test alarm" control in settings. */
  const trigger = useCallback((med) => {
    setActiveAlarm({ med, seconds: 0, key: doseAlarmKey(med?.id, 0) });
    alarmService.start();
  }, []);

  return { activeAlarm, dismiss, snooze, markTaken, trigger };
}
